<template>
  <!-- ========== HERO ========== -->
  <section id="home" class="hero">
    <!-- Background image via Vuetify v-img -->
    <div class="hero-bg-wrap">
      <v-img
        src="@/assets/background.jpg"
        cover
        height="100%"
        width="100%"
      />
    </div>
    <div class="hero-overlay" />
    <div class="hero-content text-center px-4">
      <p class="hero-kicker">Hull Zombie BJJ</p>
      <h1 class="hero-title">Start Brazilian Jiu Jitsu in Hull</h1>
      <div class="hero-rule mx-auto" />
      <p class="hero-lead">Never trained Brazilian Jiu Jitsu before? Start here.</p>
      <p class="hero-tagline">
        Whether you're completely new to martial arts, looking for a new way to get fit, or
        simply want to try something different, Hull Zombie BJJ welcomes beginners.
      </p>
      <p class="hero-price">Your first class is just <strong>£10</strong>.</p>
      <div class="hero-actions mt-8">
        <v-btn
          color="red-darken-2"
          size="x-large"
          rounded="pill"
          elevation="6"
          class="hero-btn mr-3 mb-3"
          @click="scrollTo('schedule')"
        >
          Book Your First Class
        </v-btn>
        <v-btn
          variant="outlined"
          color="white"
          size="x-large"
          rounded="pill"
          class="hero-btn mb-3"
          @click="scrollTo('contact-section')"
        >
          Message Us
        </v-btn>
      </div>
    </div>
    <div class="scroll-chevron" @click="scrollTo('beginners')">
      <v-icon size="36" class="chevron-bounce">mdi-chevron-double-down</v-icon>
      <v-img
        src="@/assets/mainlogo-transparent.png"
        max-width="60"
        class="mx-auto mt-1"
        contain
      />
    </div>
  </section>

  <!-- ========== NEW TO BJJ ========== -->
  <section id="beginners" class="section section-alt">
    <v-container class="section-container">
      <div class="section-header">
        <p class="kicker">BJJ for Beginners</p>
        <h2 class="section-title">New to BJJ? You're in the Right Place.</h2>
        <div class="section-divider mx-auto" />
      </div>
      <v-row justify="center" class="mt-12">
        <v-col cols="12" md="9">
          <div class="checklist">
            <div v-for="item in beginnerChecklist" :key="item" class="checklist-item">
              <v-icon color="red-darken-2" size="24" class="mr-3">mdi-check-circle</v-icon>
              <span>{{ item }}</span>
            </div>
          </div>
          <p class="body-text text-center mt-8">
            Our beginner-friendly training is designed to help you learn the fundamentals in a
            welcoming environment.
          </p>
          <p class="hero-price text-center mt-4">Your first class is <strong>£10</strong></p>
          <p class="body-text text-center mt-2">
            Come along, meet the team and experience Brazilian Jiu Jitsu for yourself.
          </p>
          <div class="text-center mt-8">
            <v-btn
              color="red-darken-2"
              rounded="pill"
              size="large"
              @click="scrollTo('schedule')"
            >Book Your £10 First Class</v-btn>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </section>

  <!-- ========== WORRIED ABOUT STARTING ========== -->
  <section id="worries" class="section section-dark">
    <v-container class="section-container">
      <div class="section-header">
        <h2 class="section-title">Worried About Starting?</h2>
        <div class="section-divider mx-auto" />
      </div>
      <v-row justify="center" class="mt-12">
        <v-col cols="12" md="9">
          <v-expansion-panels class="theme-panels" variant="accordion">
            <v-expansion-panel v-for="w in worries" :key="w.q">
              <v-expansion-panel-title>{{ w.q }}</v-expansion-panel-title>
              <v-expansion-panel-text>{{ w.a }}</v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </section>

  <!-- ========== WHAT TO EXPECT ========== -->
  <section id="what-to-expect" class="section section-alt">
    <v-container class="section-container">
      <div class="section-header">
        <h2 class="section-title">What Happens at Your First BJJ Class?</h2>
        <div class="section-divider mx-auto" />
      </div>
      <v-row justify="center" class="mt-12">
        <v-col cols="12" md="9" class="text-center">
          <p class="body-text">
            When you arrive, we'll get you settled in and explain what you need to know.
          </p>
          <p class="body-text mt-5">
            You'll learn some basic Brazilian Jiu Jitsu techniques and movements, practise with
            other members and get a feel for how the class works.
          </p>
          <p class="body-text mt-5">
            <strong style="color: #fff">You don't need to know anything before you arrive.</strong>
            Just turn up, listen, learn and give it a go.
          </p>
          <v-btn
            color="red-darken-2"
            rounded="pill"
            size="large"
            class="mt-8"
            @click="scrollTo('schedule')"
          >Book Your First Class for £10</v-btn>
        </v-col>
      </v-row>
    </v-container>
  </section>

  <!-- ========== WHAT IS BJJ ========== -->
  <section id="about" class="section section-dark">
    <v-container class="section-container">
      <div class="section-header">
        <h2 class="section-title">What is Brazilian Jiu-Jitsu?</h2>
        <div class="section-divider mx-auto" />
      </div>
      <v-row align="center" class="mt-12 ga-4">
        <v-col cols="12" md="6" order="2" order-md="1">
          <p class="body-text">
            Brazilian Jiu-Jitsu (BJJ) is a martial art that emphasises grappling and ground
            fighting. Known as the <strong style="color: #fff">"gentle art,"</strong> it empowers a
            smaller person to defeat a larger opponent through technique, leverage, and
            submissions — not brute force.
          </p>
          <p class="body-text mt-5">
            BJJ takes the fight to the ground, where skill and positioning beat raw strength.
            It's a technical chess match played with your entire body, and one of the most
            effective self-defence systems in the world.
          </p>
          <v-btn
            variant="outlined"
            color="red-darken-2"
            rounded="pill"
            class="mt-8"
            @click="scrollTo('schedule')"
          >Start Training Today</v-btn>
        </v-col>
        <v-col cols="12" md="6" order="1" order-md="2">
          <v-img
            src="@/images/comp.jpg"
            cover
            height="380"
            class="rounded-lg section-image"
          />
        </v-col>
      </v-row>
    </v-container>
  </section>

  <!-- ========== BENEFITS ========== -->
  <section id="benefits" class="section section-alt">
    <v-container class="section-container">
      <div class="section-header">
        <h2 class="section-title">Why Train BJJ?</h2>
        <div class="section-divider mx-auto" />
      </div>
      <v-row class="mt-12">
        <v-col
          v-for="b in benefits"
          :key="b.title"
          cols="12"
          sm="6"
          md="4"
          class="mb-4"
        >
          <div class="benefit-card pa-7 h-100">
            <v-icon :icon="b.icon" size="44" color="red-darken-2" class="mb-5 d-block" />
            <h3 class="benefit-title mb-3">{{ b.title }}</h3>
            <p class="benefit-text">{{ b.text }}</p>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </section>

  <!-- ========== COACH ========== -->
  <section id="coach" class="section section-dark">
    <v-container class="section-container">
      <div class="section-header">
        <h2 class="section-title">Meet the Coach</h2>
        <div class="section-divider mx-auto" />
      </div>
      <v-row align="center" class="mt-12 ga-6">
        <v-col cols="12" md="5" class="text-center">
          <div class="coach-photo-wrap mx-auto">
            <v-img
              src="@/images/coach.jpg"
              cover
              class="coach-photo"
            />
          </div>
          <h3 class="coach-name mt-7">Martyn Oliver</h3>
          <p class="coach-role mt-1">Head Coach — Hull Zombie BJJ</p>
        </v-col>
        <v-col cols="12" md="7">
          <p class="body-text">
            Martyn is a passionate martial artist who loves sharing his knowledge and experience
            with others. His classes are more than just training sessions — they're a chance to
            connect with like-minded people and have fun while learning valuable skills.
          </p>
          <p class="body-text mt-5">
            Expect a relaxed, friendly atmosphere where you'll feel right at home from day one.
            He focuses on the fundamentals but is always eager to explore new techniques and
            strategies. Whether you're a complete beginner or a seasoned grappler, there's
            something in every class for you.
          </p>
          <div class="mt-7 d-flex flex-wrap ga-2">
            <v-chip color="red-darken-2" variant="tonal" label>Fundamentals</v-chip>
            <v-chip color="red-darken-2" variant="tonal" label>Competition Prep</v-chip>
            <v-chip color="red-darken-2" variant="tonal" label>Self-Defence</v-chip>
            <v-chip color="red-darken-2" variant="tonal" label>All Levels Welcome</v-chip>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </section>

  <!-- ========== GALLERY ========== -->
  <section id="gallery" class="section section-alt">
    <v-container class="section-container">
      <div class="section-header">
        <h2 class="section-title">Gallery</h2>
        <div class="section-divider mx-auto" />
      </div>
      <v-carousel
        class="mt-12 rounded-lg gallery-carousel"
        height="480"
        show-arrows="hover"
        hide-delimiter-background
        cycle
        interval="4000"
        color="red-darken-2"
      >
        <v-carousel-item src="@/images/group.jpg" cover />
        <v-carousel-item src="@/images/comp.jpg" cover />
        <v-carousel-item src="@/images/group1.jpg" cover />
        <v-carousel-item src="@/images/group2.jpg" cover />
        <v-carousel-item src="@/images/group3.jpg" cover />
        <v-carousel-item src="@/images/group4.jpg" cover />
        <v-carousel-item src="@/images/group5.jpg" cover />
        <v-carousel-item src="@/images/group6.jpg" cover />
        <v-carousel-item src="@/images/group7.jpg" cover />
      </v-carousel>
    </v-container>
  </section>

  <!-- ========== SCHEDULE & PRICING ========== -->
  <section id="schedule" class="section section-dark">
    <v-container class="section-container">
      <div class="section-header">
        <p class="kicker">BJJ Classes Hull</p>
        <h2 class="section-title">Schedule &amp; Pricing</h2>
        <div class="section-divider mx-auto" />
      </div>

      <!-- Timetable -->
      <h3 class="subsection-title mt-14 mb-6">Class Timetable</h3>
      <v-row>
        <v-col
          v-for="c in classTimetable"
          :key="c.day"
          cols="12"
          md="4"
        >
          <div class="schedule-card pa-7 rounded-lg h-100">
            <div class="d-flex align-center mb-5">
              <v-icon color="red-darken-2" size="30" class="mr-3">mdi-calendar-week</v-icon>
              <span class="schedule-day">{{ c.day }}</span>
            </div>
            <div class="schedule-class-name mb-3">{{ c.className }}</div>
            <div class="schedule-info mb-2">
              <v-icon size="18" class="mr-2" style="color: rgba(255,255,255,0.4)">mdi-clock-outline</v-icon>
              {{ c.time }}
            </div>
            <div class="schedule-info">
              <v-icon size="18" class="mr-2" style="color: rgba(255,255,255,0.4)">mdi-map-marker-outline</v-icon>
              Hull Church of Ascension &mdash;
              <a
                href="https://www.google.com/maps/place/HU5+5DN"
                target="_blank"
                class="map-link ml-1"
              >HU5 5DN</a>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Pricing -->
      <h3 class="subsection-title mt-14 mb-6">Membership &amp; Class Prices</h3>
      <v-row justify="center">
        <v-col
          v-for="p in pricingPlans"
          :key="p.label"
          cols="12"
          sm="6"
          md="3"
        >
          <div :class="['pricing-card text-center pa-8 rounded-lg h-100', { 'pricing-featured': p.featured }]">
            <v-chip v-if="p.featured" color="red-darken-2" size="small" class="mb-4">Most Popular</v-chip>
            <v-icon color="red-darken-2" size="40" class="mb-5">{{ p.icon }}</v-icon>
            <div class="pricing-label">{{ p.label }}</div>
            <div class="pricing-amount pricing-amount-sm">{{ p.amount }}</div>
            <div class="pricing-desc">{{ p.desc }}</div>
            <v-btn
              color="red-darken-2"
              :variant="p.featured ? 'flat' : 'outlined'"
              rounded="pill"
              class="mt-8 pricing-btn"
              size="large"
              block
              @click="scrollTo('contact-section')"
            >{{ p.cta }}</v-btn>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </section>

  <!-- ========== PRIVATE COACHING ========== -->
  <section id="personal-training" class="section section-alt">
    <v-container class="section-container">
      <div class="section-header">
        <p class="kicker">BJJ Private Lessons Hull</p>
        <h2 class="section-title">Private BJJ Coaching</h2>
        <div class="section-divider mx-auto" />
      </div>
      <v-row justify="center" class="mt-12">
        <v-col cols="12" md="8" class="text-center">
          <p class="body-text">
            Want individual attention or extra training outside regular classes? Private BJJ
            coaching is available.
          </p>
        </v-col>
      </v-row>
      <v-row justify="center" class="mt-6">
        <v-col cols="12" sm="6" md="4">
          <div class="pricing-card mini-pricing-card text-center pa-8 rounded-lg h-100">
            <div class="pricing-label">Single Session</div>
            <div class="pricing-amount pricing-amount-sm">£30</div>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <div class="pricing-card mini-pricing-card text-center pa-8 rounded-lg h-100">
            <div class="pricing-label">4 Sessions</div>
            <div class="pricing-amount pricing-amount-sm">£100</div>
          </div>
        </v-col>
      </v-row>
      <v-row justify="center">
        <v-col cols="12" md="8" class="text-center">
          <p class="body-text mt-4">
            Private sessions can be used to work on specific techniques, improve particular areas
            of your game or get additional coaching.
          </p>
          <v-btn
            color="red-darken-2"
            rounded="pill"
            size="large"
            class="mt-8 btn-safe"
            @click="scrollTo('contact-section')"
          >Enquire About Private Coaching</v-btn>
        </v-col>
      </v-row>
    </v-container>
  </section>

  <!-- ========== ABOUT HULL ZOMBIE BJJ ========== -->
  <section id="club-about" class="section section-dark">
    <v-container class="section-container">
      <div class="section-header">
        <h2 class="section-title">Brazilian Jiu Jitsu in Hull</h2>
        <div class="section-divider mx-auto" />
      </div>
      <v-row justify="center" class="mt-12">
        <v-col cols="12" md="9" class="text-center">
          <p class="body-text">
            Hull Zombie BJJ is a Brazilian Jiu Jitsu club based in Hull, welcoming people who
            want to learn, train and improve.
          </p>
          <p class="body-text mt-5">
            Whether you're completely new to BJJ or already have experience, our aim is to
            provide quality training in a friendly and supportive environment.
          </p>
        </v-col>
      </v-row>
    </v-container>
  </section>

  <!-- ========== BOTTOM CTA ========== -->
  <section id="ready-to-try" class="section section-alt cta-section">
    <v-container class="section-container">
      <div class="text-center">
        <h2 class="section-title">Ready to Give BJJ a Try?</h2>
        <div class="section-divider mx-auto" />
        <p class="cta-line mt-8">You've got nothing to prove.</p>
        <p class="cta-line">You've just got to walk through the door.</p>
        <p class="hero-price mt-4">Your first class is <strong>£10</strong>.</p>
        <div class="hero-actions mt-8">
          <v-btn
            color="red-darken-2"
            size="x-large"
            rounded="pill"
            elevation="6"
            class="hero-btn mr-3 mb-3"
            @click="scrollTo('schedule')"
          >Book Your First Class</v-btn>
          <v-btn
            variant="outlined"
            color="white"
            size="x-large"
            rounded="pill"
            class="hero-btn mb-3"
            @click="scrollTo('contact-section')"
          >Message Us to Get Started</v-btn>
        </div>
      </div>
    </v-container>
  </section>

  <!-- ========== FAQ ========== -->
  <section id="faq" class="section section-dark">
    <v-container class="section-container">
      <div class="section-header">
        <p class="kicker">BJJ Hull — FAQ</p>
        <h2 class="section-title">Frequently Asked Questions</h2>
        <div class="section-divider mx-auto" />
      </div>
      <v-row justify="center" class="mt-12">
        <v-col cols="12" md="9">
          <v-expansion-panels class="theme-panels" variant="accordion">
            <v-expansion-panel v-for="f in faqs" :key="f.q">
              <v-expansion-panel-title>{{ f.q }}</v-expansion-panel-title>
              <v-expansion-panel-text>{{ f.a }}</v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </section>

  <!-- ========== FIND US ========== -->
  <section id="contact-section" class="section section-contact">
    <v-container class="section-container">
      <div class="section-header">
        <h2 class="section-title">Find Us</h2>
        <div class="section-divider mx-auto" />
      </div>
      <v-row justify="center" class="mt-12">
        <v-col cols="12" md="7" class="text-center">
          <v-icon size="52" color="red-darken-2" class="mb-5">mdi-map-marker</v-icon>
          <h3 class="contact-location-title">Hull Church of Ascension</h3>
          <a
            href="https://www.google.com/maps/place/HU5+5DN"
            target="_blank"
            class="map-link contact-location-addr d-block mt-2"
          >HU5 5DN, Hull, East Yorkshire</a>
          <p class="body-text mt-6">
            Classes are open to everyone — no experience needed.<br />
            Just show up and get on the mats.
          </p>
          <p class="body-text mt-4">
            Prefer to message first? Reach out on Instagram or Facebook and we'll get back to
            you.
          </p>
          <div class="mt-10 d-flex justify-center ga-3">
            <v-btn
              :icon="true"
              href="https://www.instagram.com/hullzombie.bjj/"
              target="_blank"
              variant="outlined"
              color="rgba(255,255,255,0.3)"
              size="large"
              rounded="circle"
            >
              <v-icon size="22">mdi-instagram</v-icon>
            </v-btn>
            <v-btn
              :icon="true"
              href="https://www.facebook.com/martyn.oliver.75"
              target="_blank"
              variant="outlined"
              color="rgba(255,255,255,0.3)"
              size="large"
              rounded="circle"
            >
              <v-icon size="22">mdi-facebook</v-icon>
            </v-btn>
            <v-btn
              :icon="true"
              href="https://www.tiktok.com/@hullzombie.bjj"
              target="_blank"
              variant="outlined"
              color="rgba(255,255,255,0.3)"
              size="large"
              rounded="circle"
            >
              <v-icon size="22">
                <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
                  <path
                    d="M12.74,2C12.74,2 12.75,6.79 17.03,7.14V9.33C15.44,9.22 14.26,8.54 13.48,7.41V14.9C13.48,18.11 11.33,20.17 8.5,20.17C5.67,20.17 3.52,18.1 3.52,14.9C3.52,12.02 5.42,9.95 8.03,9.65V12.05C6.98,12.32 6.27,13.18 6.27,14.25C6.27,15.5 7.18,16.41 8.48,16.41C9.78,16.41 10.67,15.53 10.67,14.2V2H12.74Z"
                  />
                </svg>
              </v-icon>
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </section>
</template>

<script>
import { defineComponent } from "vue";

export default defineComponent({
  name: "HomeView",
  setup() {
    const benefits = [
      {
        icon: "mdi-shield-check",
        title: "Self-Defense",
        text: "Learn practical techniques that work regardless of size or strength. Build real, lasting confidence.",
      },
      {
        icon: "mdi-dumbbell",
        title: "Physical Fitness",
        text: "A complete full-body workout improving strength, flexibility, and cardiovascular endurance — and it's genuinely fun.",
      },
      {
        icon: "mdi-brain",
        title: "Mental Toughness",
        text: "Stay calm under pressure, think strategically, and problem-solve in real-time. Resilience you'll carry into everyday life.",
      },
      {
        icon: "mdi-account-group",
        title: "Community",
        text: "More than a martial art — it's a family. A supportive crew where you'll make lifelong friends on and off the mat.",
      },
      {
        icon: "mdi-spa",
        title: "Stress Relief",
        text: "A healthy outlet for pent-up energy. Roll, sweat, and leave every session feeling lighter and fully recharged.",
      },
      {
        icon: "mdi-trophy",
        title: "Competition",
        text: "Put your skills to the test at local and national events — or simply enjoy the journey and daily improvement.",
      },
    ];

    const beginnerChecklist = [
      "You don't need previous martial arts experience.",
      "You don't need to be extremely fit.",
      "You don't need to know anyone.",
      "And you don't need to be good at Brazilian Jiu Jitsu before you start.",
    ];

    const worries = [
      {
        q: "“I've never done anything like this before.”",
        a: "That's absolutely fine. Everyone starts somewhere.",
      },
      {
        q: "“I'm not very fit.”",
        a: "You don't need to be in great shape before you begin. Your fitness will develop as you train.",
      },
      {
        q: "“I don't know anyone.”",
        a: "You don't need to. You'll meet people when you come along.",
      },
      {
        q: "“I'm nervous about getting thrown straight into fighting.”",
        a: "Your first class is about learning and getting comfortable with the basics. You don't have to walk in already knowing what you're doing.",
      },
      {
        q: "“What if everyone is better than me?”",
        a: "Everyone in the room has had a first class. You'll be taught the fundamentals and supported as you learn.",
      },
    ];

    const classTimetable = [
      {
        day: "Tuesday",
        className: "BJJ Beginners Class",
        time: "7:30 PM – 8:30 PM",
      },
      {
        day: "Friday",
        className: "BJJ Beginners Class",
        time: "6:00 PM – 7:00 PM",
      },
      {
        day: "Saturday",
        className: "BJJ Beginners Class",
        time: "8:00 AM – 9:00 AM",
      },
    ];

    const pricingPlans = [
      {
        icon: "mdi-cash",
        label: "First Class",
        amount: "£10",
        desc: "Your first class at Hull Zombie BJJ.",
        cta: "Book Now",
        featured: false,
      },
      {
        icon: "mdi-cash-multiple",
        label: "Pay As You Go",
        amount: "£10",
        desc: "Per class — no commitment.",
        cta: "Get Started",
        featured: false,
      },
      {
        icon: "mdi-calendar-month",
        label: "Standard Membership",
        amount: "£60/mo",
        desc: "2 classes per week.",
        cta: "Get Started",
        featured: true,
      },
      {
        icon: "mdi-calendar-star",
        label: "Full Membership",
        amount: "£75/mo",
        desc: "3 classes per week.",
        cta: "Get Started",
        featured: false,
      },
    ];

    const faqs = [
      {
        q: "Do I need experience?",
        a: "No. Beginners are welcome and you don't need any previous Brazilian Jiu Jitsu or martial arts experience.",
      },
      {
        q: "Do I need to be fit?",
        a: "No. You can start at your current fitness level and build your fitness as you train.",
      },
      {
        q: "What should I wear?",
        a: "Please contact us before your first class if you're unsure what to wear or bring.",
      },
      {
        q: "How much is my first class?",
        a: "Your first class is £10.",
      },
      {
        q: "Do you offer free trials?",
        a: "No. The first class is £10.",
      },
      {
        q: "How much is membership?",
        a: "Standard membership is £60 per month for 2 classes per week. Full membership is £75 per month for 3 classes per week.",
      },
      {
        q: "Can I pay as I go?",
        a: "Yes. Pay-as-you-go classes are £10.",
      },
      {
        q: "Do you offer private training?",
        a: "Yes. Private training is £30 for a single session or £100 for 4 sessions.",
      },
    ];

    const scrollTo = (id) => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    };

    return {
      benefits,
      beginnerChecklist,
      worries,
      classTimetable,
      pricingPlans,
      faqs,
      scrollTo,
    };
  },
});
</script>

<style>
/* ===== GLOBAL ===== */
html {
  scroll-behavior: smooth;
}

/* ===== HERO ===== */
.hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.hero-bg-wrap {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  z-index: 1;
}

.hero-content {
  position: relative;
  z-index: 2;
  max-width: 720px;
  width: 100%;
}

.hero-kicker {
  font-size: 0.8rem;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 6px;
}

.hero-title {
  font-size: clamp(2rem, 5.5vw, 3.6rem);
  font-weight: 900;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #fff;
  line-height: 1.15;
}

.hero-rule {
  width: 80px;
  height: 4px;
  background: #c62828;
  border-radius: 2px;
  margin: 22px auto 22px;
}

.hero-subtitle {
  font-size: clamp(0.65rem, 1.8vw, 0.88rem);
  letter-spacing: 4px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
}

.hero-lead {
  font-size: clamp(1.1rem, 2.6vw, 1.35rem);
  color: #ffffff;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.hero-tagline {
  font-size: 1.02rem;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 14px;
}

.hero-price {
  font-size: 1.2rem;
  color: #ef5350;
  font-weight: 700;
  margin-top: 16px;
  letter-spacing: 0.5px;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
}

.hero-btn,
.btn-safe {
  white-space: normal !important;
  height: auto !important;
  min-height: 48px;
}

.hero-btn .v-btn__content,
.btn-safe .v-btn__content {
  white-space: normal;
  text-align: center;
}

@media (max-width: 420px) {
  .hero-btn,
  .btn-safe {
    font-size: 0.82rem;
    padding: 10px 18px !important;
  }
}

.scroll-chevron {
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.5);
  text-align: center;
}

.chevron-bounce {
  animation: chevBounce 2.4s ease-in-out infinite;
}

@keyframes chevBounce {
  0%, 100% { transform: translateY(0); opacity: 0.5; }
  50% { transform: translateY(10px); opacity: 1; }
}

/* ===== SECTIONS ===== */
.section {
  padding: 90px 0;
}

.section-container {
  max-width: 1040px !important;
}

.section-dark {
  background: #0d1520;
}

.section-alt {
  background: #111d2c;
}

.section-contact {
  background: #090e19;
}

.section-header {
  text-align: center;
}

.kicker {
  font-size: 0.78rem;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #ef5350;
  font-weight: 700;
  margin-bottom: 8px;
}

.section-title {
  font-size: clamp(1.6rem, 4vw, 2.6rem);
  font-weight: 900;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: #ffffff;
}

.section-divider {
  width: 60px;
  height: 4px;
  background: #c62828;
  border-radius: 2px;
  margin-top: 16px !important;
}

.subsection-title {
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.7);
}

.body-text {
  font-size: 1.05rem;
  line-height: 1.85;
  color: rgba(255, 255, 255, 0.65);
}

.section-image {
  border: 1px solid rgba(255, 255, 255, 0.07);
}

/* ===== BEGINNER CHECKLIST ===== */
.checklist {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.checklist-item {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 10px;
  padding: 16px 20px;
  font-size: 1.02rem;
  color: rgba(255, 255, 255, 0.8);
}

/* ===== ACCORDION (WORRIES / FAQ) ===== */
.theme-panels {
  background: transparent !important;
}

.theme-panels .v-expansion-panel {
  background: rgba(255, 255, 255, 0.035) !important;
  color: rgba(255, 255, 255, 0.85) !important;
  border: 1px solid rgba(255, 255, 255, 0.07) !important;
  margin-bottom: 10px;
  border-radius: 10px !important;
}

.theme-panels .v-expansion-panel-title {
  font-weight: 700;
  font-size: 1rem;
}

.theme-panels .v-expansion-panel-text {
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.98rem;
  line-height: 1.7;
}

.theme-panels .v-icon {
  color: #ef5350 !important;
}

/* ===== BENEFITS ===== */
.benefit-card {
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 12px;
  transition: border-color 0.3s ease, transform 0.3s ease;
}

.benefit-card:hover {
  border-color: rgba(198, 40, 40, 0.7);
  transform: translateY(-5px);
}

.benefit-title {
  font-size: 1rem;
  font-weight: 700;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 1.5px;
}

.benefit-text {
  font-size: 0.9rem;
  line-height: 1.72;
  color: rgba(255, 255, 255, 0.55);
}

/* ===== COACH ===== */
.coach-photo-wrap {
  width: 280px;
  height: 280px;
  border-radius: 50%;
  overflow: hidden;
  border: 4px solid #c62828;
  box-shadow: 0 0 40px rgba(198, 40, 40, 0.25);
}

.coach-photo {
  width: 100% !important;
  height: 100% !important;
}

.coach-name {
  font-size: 1.75rem;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: 1px;
}

.coach-role {
  color: #c62828;
  font-size: 0.85rem;
  letter-spacing: 2px;
  text-transform: uppercase;
}

/* ===== GALLERY ===== */
.gallery-carousel {
  border: 1px solid rgba(255, 255, 255, 0.07);
}

/* ===== SCHEDULE ===== */
.schedule-card {
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-left: 4px solid #c62828;
  transition: background 0.3s ease;
}

.schedule-card:hover {
  background: rgba(255, 255, 255, 0.065);
}

.schedule-day {
  font-size: 1.25rem;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.schedule-class-name {
  font-size: 1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
}

.schedule-info {
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.55);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.map-link {
  color: #ef5350;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s;
}

.map-link:hover {
  color: #ffffff;
  text-decoration: underline;
}

/* ===== PRICING ===== */
.pricing-card {
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  transition: transform 0.3s ease;
}

.pricing-card:hover {
  transform: translateY(-6px);
}

.pricing-featured {
  border: 2px solid #c62828;
  background: rgba(198, 40, 40, 0.07);
  box-shadow: 0 8px 40px rgba(198, 40, 40, 0.18);
}

.mini-pricing-card {
  max-width: 260px;
  margin: 0 auto;
}

.pricing-btn {
  white-space: normal !important;
  height: auto !important;
  min-height: 44px;
}

.pricing-label {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 3px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 10px;
}

.pricing-amount {
  font-size: 4rem;
  font-weight: 900;
  color: #ffffff;
  line-height: 1;
  margin-bottom: 10px;
}

.pricing-amount-sm {
  font-size: 2.6rem;
}

.pricing-desc {
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.45);
  letter-spacing: 0.5px;
}

/* ===== BOTTOM CTA ===== */
.cta-line {
  font-size: 1.15rem;
  color: rgba(255, 255, 255, 0.75);
  font-weight: 600;
}

/* ===== CONTACT ===== */
.contact-location-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
}

.contact-location-addr {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.55);
}
</style>
